import { checkoutBlockCode } from './errorPresentation';
import { tr, useCurrentLocale } from '../i18n';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { fetchFunnelOffer, fetchOrderStatus, submitFunnelStep } from './api';
import { authenticatePayment } from './stripe-action';
import type { CheckoutVoucher, FunnelNavigationTarget, FunnelOffer } from './api';
import {
  clearFunnelHandoff,
  externalFunnelUrl,
  hostedFunnelUrl,
  isCompleteHandoff,
  persistFunnelHandoff,
  readFunnelHandoff,
  readFunnelCompletion,
  restoreFunnelHandoff,
  thankYouUrl,
} from './external-funnel';
import { UpsellStep } from './UpsellStep';
import { CheckoutErrorAlert } from './CheckoutErrorAlert';
import type { CheckoutErrorDescriptor } from './errorPresentation';
import {
  presentCheckoutOrderError,
  CheckoutFlowError,
  presentLocalizedError,
  presentSystemError,
  resolveCheckoutErrorMessages,
} from './errorPresentation';
import { FunnelConfirmation } from './FunnelConfirmation';
import { useFeatureFlag } from '../shared/feature-flags/useFeatureFlag';
import { ensureUpsellWidgetFont, resolveUpsellWidgetStyle, upsellWidgetCssVariables } from './upsell-widget-style';
import type { FunnelCompletion } from './external-funnel';
import './checkout.css';

type OfferState =
  | 'checking-storage'
  | 'storage-required'
  | 'storage-denied'
  | 'loading'
  | 'empty'
  | FunnelOffer;
type EmbedThemeMode = 'light' | 'dark';
type EmbedColor = Readonly<{
  red: number;
  green: number;
  blue: number;
  alpha: number;
}>;
interface EmbedVisualContext {
  mode: EmbedThemeMode;
  fontFamily?: string;
  fontSize?: number;
  textColor?: EmbedColor;
  mutedColor?: EmbedColor;
  accentColor?: EmbedColor;
  accentStrongColor?: EmbedColor;
  borderColor?: EmbedColor;
  radius?: number;
  spacing?: number;
}
const FUNNEL_PENDING_STORAGE_PREFIX = 'vendepay:funnel-pending:';

interface StorageAccessDocument {
  hasStorageAccess?: () => Promise<boolean>;
  requestStorageAccess?: () => Promise<void>;
}

export function UpsellEmbed({ standalone = false }: { standalone?: boolean }) {
  useCurrentLocale();
  const upsellHostErrorToastEnabled = useFeatureFlag('upsellHostErrorToast');
  const [initial] = useState(() => initialEmbedState(standalone));
  const handoff = initial.handoff;
  const [completion, setCompletion] = useState<FunnelCompletion | null>(initial.completion);
  const [completed, setCompleted] = useState(initial.completed);
  const nodeId = handoff?.nodeId ?? '';
  const funnelHandoffId = handoff?.handoffId ?? '';
  const [offer, setOffer] = useState<OfferState>(completed ? 'empty' : 'checking-storage');
  const [loadError, setLoadError] = useState<CheckoutErrorDescriptor | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<CheckoutErrorDescriptor | null>(initial.pending && !initial.pending.voucher
    ? presentLocalizedError({ key: 'error.offerProcessing', kind: 'payment', action: 'retry' }) : null);
  const [voucher, setVoucher] = useState<CheckoutVoucher | null>(initial.pending?.voucher ?? null);
  const [pendingSaleId, setPendingSaleId] = useState<string | null>(initial.pending?.saleId ?? null);
  const [navigation, setNavigation] = useState<FunnelNavigationTarget | null>(null);
  const [embedContext, setEmbedContext] = useState<EmbedVisualContext>(() => ({
    mode: 'light',
  }));
  const [hostSupportsErrorToast, setHostSupportsErrorToast] = useState(false);
  const forwardedErrorRef = useRef<CheckoutErrorDescriptor | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const productStyleEnabled = useFeatureFlag('upsellWidgetStyle');
  const snippetOffer = typeof offer === 'object' && offer.node.mode === 'snippet' ? offer : null;
  // With the flag on, the seller's product style replaces anything detected on the host page.
  // Standalone pages also render hosted nodes, which keep their current look.
  const widgetStyle = productStyleEnabled && (!standalone || snippetOffer !== null)
    ? resolveUpsellWidgetStyle(snippetOffer?.widgetStyle)
    : null;
  const embedClassName = widgetStyle
    ? `vp-root vp-embed vp-embed--styled vp-embed--${widgetStyle.theme}`
    : `vp-root vp-embed vp-embed--${embedContext.mode}`;
  const embedStyle = widgetStyle ? upsellWidgetCssVariables(widgetStyle) : embedContextStyle(embedContext);
  const widgetFont = widgetStyle?.font;

  useEffect(() => {
    if (widgetFont !== undefined) ensureUpsellWidgetFont(widgetFont);
  }, [widgetFont]);

  useEffect(() => {
    if (initial.consumeHash) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    }
  }, [initial.consumeHash]);

  useEffect(() => {
    if (standalone) return;

    document.body.classList.add('vp-embed-body');

    function onParentTheme(event: MessageEvent<unknown>) {
      if (event.source !== window.parent) return;
      if (typeof event.data === 'object' && event.data !== null && 'type' in event.data
        && event.data.type === 'vp-upsell-host' && 'errorToast' in event.data && event.data.errorToast === true) {
        setHostSupportsErrorToast(true);
        return;
      }
      const context = parseEmbedVisualContext(event.data);
      if (context === null) return;
      setEmbedContext(context);
    }

    window.addEventListener('message', onParentTheme);
    window.parent?.postMessage({ type: 'vp-upsell-ready' }, '*');
    return () => {
      document.body.classList.remove('vp-embed-body');
      window.removeEventListener('message', onParentTheme);
    };
  }, [standalone]);

  useEffect(() => {
    if (standalone) return;
    if (!upsellHostErrorToastEnabled || !hostSupportsErrorToast) {
      if (forwardedErrorRef.current !== null) {
        window.parent?.postMessage({ type: 'vp-upsell-error', error: null }, '*');
        forwardedErrorRef.current = null;
      }
      return;
    }
    if (error === null) {
      if (forwardedErrorRef.current !== null) {
        window.parent?.postMessage({ type: 'vp-upsell-error', error: null }, '*');
        forwardedErrorRef.current = null;
      }
      return;
    }
    if (forwardedErrorRef.current === error) return;
    const messages = resolveCheckoutErrorMessages(error);
    window.parent?.postMessage({ type: 'vp-upsell-error', error: messages }, '*');
    forwardedErrorRef.current = error;
  }, [error, hostSupportsErrorToast, standalone, upsellHostErrorToastEnabled]);

  useEffect(() => {
    let active = true;
    if (completed || !nodeId || !funnelHandoffId) {
      setOffer('empty');
      setLoadError(null);
      return;
    }
    setLoadError(null);
    void loadOfferWithStorageCheck(standalone, nodeId, funnelHandoffId)
      .then((result) => {
        if (active) setOffer(result);
      })
      .catch(() => {
        if (!active) return;
        setOffer('empty');
        setLoadError(presentLocalizedError({ key: 'error.offerProcessing', kind: 'system', action: 'retry' }));
      });
    return () => {
      active = false;
    };
  }, [completed, nodeId, funnelHandoffId, standalone, loadAttempt]);

  useEffect(() => {
    function postHeight() {
      const height = rootRef.current?.offsetHeight ?? 0;
      window.parent?.postMessage({ type: 'vp-upsell-height', height }, '*');
    }
    postHeight();
    const observer = new ResizeObserver(postHeight);
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  });

  useEffect(() => {
    if (navigation === null || typeof offer !== 'object') return;
    clearPendingFunnel(funnelHandoffId, offer.node.id);
    if (!standalone) {
      window.parent?.postMessage(
        { type: 'vp-upsell-done', navigation, saleId: offer.originalSaleId, checkoutId: offer.checkoutId },
        '*',
      );
      return;
    }

    if (navigation.type === 'external') {
      persistFunnelHandoff({ nodeId: navigation.nodeId, handoffId: funnelHandoffId });
      const target = externalFunnelUrl(
        navigation.url,
        navigation.nodeId,
        funnelHandoffId,
        offer.originalSaleId,
      );
      if (target !== null) window.location.assign(target);
      else {
        clearFunnelHandoff();
        setCompletion({ checkoutId: offer.checkoutId, saleId: offer.originalSaleId, handoffId: funnelHandoffId });
        setCompleted(true);
      }
      return;
    }
    if (navigation.type === 'hosted') {
      persistFunnelHandoff({ nodeId: navigation.nodeId, handoffId: funnelHandoffId });
      window.location.assign(hostedFunnelUrl(window.location.origin, navigation.nodeId, funnelHandoffId));
      return;
    }
    clearFunnelHandoff();
    const target = thankYouUrl(navigation.url, offer.originalSaleId);
    if (target !== null) window.location.assign(target);
    else {
      setCompletion({ checkoutId: offer.checkoutId, saleId: offer.originalSaleId, handoffId: funnelHandoffId });
      setCompleted(true);
    }
  }, [funnelHandoffId, navigation, offer, standalone]);

  const checkoutId = typeof offer === 'object' ? offer.checkoutId : undefined;

  useEffect(() => {
    if (busy || navigation !== null || pendingSaleId === null || checkoutId === undefined || typeof offer !== 'object') {
      return;
    }
    let active = true;
    const timeout = voucher === null ? window.setTimeout(() => {
      if (!active) return;
      setError(presentLocalizedError({ key: 'error.offerProcessing', kind: 'payment', action: 'retry' }));
    }, 5 * 60 * 1000) : undefined;
    const interval = window.setInterval(async () => {
      try {
        const status = await fetchOrderStatus(checkoutId, pendingSaleId, funnelHandoffId);
        if (!active) return;
        if (status === 'PAID') {
          window.clearTimeout(timeout);
          window.clearInterval(interval);
          setVoucher(null);
          setPendingSaleId(null);
          clearPendingFunnel(funnelHandoffId, offer.node.id);
          setError(null);
          setNavigation(offer.navigation.accept);
        } else if (status === 'FAILED') {
          window.clearTimeout(timeout);
          window.clearInterval(interval);
          setVoucher(null);
          setPendingSaleId(null);
          clearPendingFunnel(funnelHandoffId, offer.node.id);
          setError(presentLocalizedError({ key: voucher ? 'error.pixOfferFailed' : 'error.offerComplete', kind: 'payment', action: 'retry' }));
        }
      } catch {
        return;
      }
    }, 4000);
    return () => {
      active = false;
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [busy, pendingSaleId, checkoutId, funnelHandoffId, offer, voucher, navigation]);

  async function decide(decision: 'ACCEPT' | 'REJECT') {
    if (busy || typeof offer !== 'object') return;
    setBusy(true);
    setError(null);
    try {
      // Retain the replay identity even if the decision response is lost.
      if (decision === 'ACCEPT') persistPendingFunnel(funnelHandoffId, offer.node.id, pendingSaleId, voucher);
      const result = await submitFunnelStep(offer.checkoutId, {
        originalSaleId: offer.originalSaleId,
        funnelNodeId: offer.node.id,
        decision,
        funnelHandoffId,
      });
      if (!result.accepted) {
        clearPendingFunnel(funnelHandoffId, offer.node.id);
        setNavigation(offer.navigation.reject);
        return;
      }
      if (result.aberto === false) {
        if (checkoutBlockCode(result) === 'PRICING_TOKEN_INVALIDO') {
          const refreshed = await fetchFunnelOffer(offer.node.id, funnelHandoffId);
          if (refreshed !== null) {
            setOffer(refreshed);
            clearPendingFunnel(funnelHandoffId, offer.node.id);
            setError(presentLocalizedError({ key: 'error.priceRefreshed', kind: 'payment', action: 'retry' }));
            return;
          }
        }
        if (checkoutBlockCode(result) === 'COMPRA_NAO_PERMITIDA') {
          clearPendingFunnel(funnelHandoffId, offer.node.id);
        }
        setError(presentCheckoutOrderError(result, 'error.offerUnavailable'));
        return;
      }
      if (result.status === 'PAID') {
        clearPendingFunnel(funnelHandoffId, offer.node.id);
        setNavigation(offer.navigation.accept);
        return;
      }
      if (result.status === 'PENDING' || result.status === 'REQUIRES_ACTION') {
        const nextVoucher = result.voucher ?? null;
        const nextSaleId = result.saleId ?? null;
        setVoucher(nextVoucher);
        setPendingSaleId(nextSaleId);
        if (nextSaleId !== null) {
          persistPendingFunnel(funnelHandoffId, offer.node.id, nextSaleId, nextVoucher);
        }
        if (result.status === 'REQUIRES_ACTION') {
          await authenticatePayment({
            ...(result.clientSecret ? { clientSecret: result.clientSecret } : {}),
            ...(result.publishableKey ? { publishableKey: result.publishableKey } : {}),
          });
        }
        return;
      }
      if (result.status === 'FAILED') clearPendingFunnel(funnelHandoffId, offer.node.id);
      setError(presentLocalizedError({ key: 'error.offerComplete', kind: 'payment', action: 'retry' }));
    } catch (failure) {
      setError(failure instanceof CheckoutFlowError ? failure.descriptor : presentSystemError(undefined, 'retry'));
    } finally {
      setBusy(false);
    }
  }

  function skip() {
    if (pendingSaleId !== null && typeof offer === 'object') {
      clearPendingFunnel(funnelHandoffId, offer.node.id);
      setVoucher(null);
      setPendingSaleId(null);
      setNavigation(offer.navigation.pendingSkip);
      return;
    }
    void decide('REJECT');
  }

  async function grantStorageAccess() {
    const storageDocument = document as StorageAccessDocument;
    if (storageDocument.requestStorageAccess === undefined) {
      setOffer('storage-denied');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await storageDocument.requestStorageAccess();
      setOffer('loading');
      const result = await fetchFunnelOffer(nodeId, funnelHandoffId);
      setOffer(result ?? 'storage-denied');
    } catch {
      setOffer('storage-denied');
    } finally {
      setBusy(false);
    }
  }

  function completeWithoutOffer() {
    clearFunnelHandoff();
    window.parent?.postMessage(
      { type: 'vp-upsell-done', navigation: { type: 'complete', url: '' } },
      '*',
    );
  }

  function continueInHostedCheckout() {
    window.parent?.postMessage(
      { type: 'vp-upsell-done', navigation: { type: 'hosted', nodeId } },
      '*',
    );
  }

  if (completed) {
    return (
      <div className="vp-root" ref={rootRef}>
        <FunnelConfirmation completion={completion} />
      </div>
    );
  }

  if (offer === 'checking-storage' || offer === 'loading') {
    return (
      <div className={embedClassName} style={embedStyle} ref={rootRef}>
        <div className="vp-spinner" aria-hidden="true" />
      </div>
    );
  }

  if (loadError !== null) {
    return (
      <div className={embedClassName} style={embedStyle} ref={rootRef}>
        <main className="vp-shell vp-shell--center">
          <section className="vp-card vp-state-card">
            <CheckoutErrorAlert
              error={loadError}
              placement="toast"
              autoFocus
              onAction={() => {
                setLoadError(null);
                setOffer('loading');
                setLoadAttempt((attempt) => attempt + 1);
              }}
            />
          </section>
        </main>
      </div>
    );
  }

  if (offer === 'storage-required' || offer === 'storage-denied') {
    return (
      <div className={embedClassName} style={embedStyle} ref={rootRef}>
        <main className="vp-shell vp-shell--center">
          <section className="vp-card vp-storage-access">
            <span className="vp-upsell-badge">{tr('upsell.protectedOffer')}</span>
            <h1 className="vp-upsell-title">
              {offer === 'storage-required' ? tr('upsell.storageTitle') : tr('upsell.storageDeniedTitle')}
            </h1>
            <p className="vp-upsell-subtext">
              {offer === 'storage-required'
                ? tr('upsell.storageBody')
                : tr('upsell.storageDeniedBody')}
            </p>
            {offer === 'storage-required' && (
              <button type="button" className="vp-cta" onClick={() => void grantStorageAccess()} disabled={busy}>
                {busy ? tr('upsell.loading') : tr('upsell.loadOffer')}
              </button>
            )}
            {offer === 'storage-denied' && (
              <button type="button" className="vp-cta" onClick={continueInHostedCheckout} disabled={busy}>
                {tr('upsell.openHosted')}
              </button>
            )}
            <button type="button" className="vp-upsell-decline vp-link" onClick={completeWithoutOffer} disabled={busy}>
              {tr('upsell.continueWithout')}
            </button>
          </section>
        </main>
      </div>
    );
  }

  if (offer === 'empty' || navigation !== null) {
    return <div className={embedClassName} style={embedStyle} ref={rootRef} />;
  }

  return (
    <div className={embedClassName} style={embedStyle} ref={rootRef}>
      <UpsellStep
        node={offer.node}
        busy={busy || (pendingSaleId !== null && voucher === null && error === null)}
        error={!standalone && upsellHostErrorToastEnabled && hostSupportsErrorToast ? null : error}
        voucher={voucher}
        paymentMethod="card"
        onAccept={() => decide('ACCEPT')}
        onReject={() => decide('REJECT')}
        onErrorAction={() => void decide('ACCEPT')}
        onSkip={skip}
      />
    </div>
  );
}

function parseEmbedVisualContext(data: unknown): EmbedVisualContext | null {
  if (typeof data !== 'object' || data === null) return null;
  const candidate = data as {
    type?: unknown;
    mode?: unknown;
    fontFamily?: unknown;
    fontSize?: unknown;
    textColor?: unknown;
    mutedColor?: unknown;
    accentColor?: unknown;
    accentStrongColor?: unknown;
    borderColor?: unknown;
    radius?: unknown;
    spacing?: unknown;
  };
  if (candidate.type !== 'vp-upsell-theme' || (candidate.mode !== 'light' && candidate.mode !== 'dark')) {
    return null;
  }
  const context: EmbedVisualContext = { mode: candidate.mode };
  const fontFamily = parseEmbedFontFamily(candidate.fontFamily);
  const fontSize = parseEmbedNumber(candidate.fontSize, 12, 24);
  const textColor = parseEmbedColor(candidate.textColor);
  const mutedColor = parseEmbedColor(candidate.mutedColor);
  const accentColor = parseEmbedColor(candidate.accentColor);
  const accentStrongColor = parseEmbedColor(candidate.accentStrongColor);
  const borderColor = parseEmbedColor(candidate.borderColor);
  const radius = parseEmbedNumber(candidate.radius, 0, 32);
  const spacing = parseEmbedNumber(candidate.spacing, 4, 16);

  if (fontFamily !== undefined) context.fontFamily = fontFamily;
  if (fontSize !== undefined) context.fontSize = fontSize;
  if (textColor !== undefined) context.textColor = textColor;
  if (mutedColor !== undefined) context.mutedColor = mutedColor;
  if (accentColor !== undefined) context.accentColor = accentColor;
  if (accentStrongColor !== undefined) context.accentStrongColor = accentStrongColor;
  if (borderColor !== undefined) context.borderColor = borderColor;
  if (radius !== undefined) context.radius = radius;
  if (spacing !== undefined) context.spacing = spacing;
  return context;
}

function parseEmbedColor(value: unknown): EmbedColor | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const candidate = value as { red?: unknown; green?: unknown; blue?: unknown; alpha?: unknown };
  const red = candidate.red;
  const green = candidate.green;
  const blue = candidate.blue;
  const alpha = candidate.alpha;
  if (
    typeof red !== 'number' ||
    typeof green !== 'number' ||
    typeof blue !== 'number' ||
    typeof alpha !== 'number' ||
    !Number.isFinite(red) ||
    !Number.isFinite(green) ||
    !Number.isFinite(blue) ||
    !Number.isFinite(alpha) ||
    red < 0 ||
    red > 255 ||
    green < 0 ||
    green > 255 ||
    blue < 0 ||
    blue > 255 ||
    alpha < 0 ||
    alpha > 1
  ) {
    return undefined;
  }
  return { red: Math.round(red), green: Math.round(green), blue: Math.round(blue), alpha };
}

function parseEmbedFontFamily(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const fontFamily = value.trim();
  return fontFamily.length > 0 && fontFamily.length <= 180 && /^[a-zA-Z0-9\s,'"._-]+$/.test(fontFamily)
    ? fontFamily
    : undefined;
}

function parseEmbedNumber(value: unknown, min: number, max: number): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max ? value : undefined;
}

function embedContextStyle(context: EmbedVisualContext): CSSProperties {
  const style = {} as CSSProperties & Record<string, string>;
  if (context.fontFamily) style['--vp-font'] = context.fontFamily;
  if (context.fontSize !== undefined) style['--vp-font-size'] = `${context.fontSize}px`;
  if (context.textColor) style['--vp-text'] = embedColorToCss(context.textColor);
  if (context.mutedColor) style['--vp-text-muted'] = embedColorToCss(context.mutedColor);
  if (context.accentColor) {
    style['--vp-accent'] = embedColorToCss(context.accentColor);
    style['--vp-accent-soft'] = embedColorToCss({ ...context.accentColor, alpha: 0.12 });
    style['--vp-accent-ring'] = embedColorToCss({ ...context.accentColor, alpha: 0.32 });
  }
  if (context.accentStrongColor) style['--vp-accent-strong'] = embedColorToCss(context.accentStrongColor);
  if (context.borderColor) {
    style['--vp-border'] = embedColorToCss(context.borderColor);
    style['--vp-border-strong'] = embedColorToCss({ ...context.borderColor, alpha: Math.min(1, context.borderColor.alpha + 0.14) });
  }
  if (context.radius !== undefined) {
    style['--vp-radius-input'] = `${context.radius}px`;
    style['--vp-radius'] = `${context.radius}px`;
    style['--vp-radius-bump'] = `${context.radius}px`;
  }
  if (context.spacing !== undefined) style['--vp-embed-space'] = `${context.spacing}px`;
  return style;
}

function embedColorToCss(color: EmbedColor): string {
  const red = Math.round(color.red);
  const green = Math.round(color.green);
  const blue = Math.round(color.blue);
  const alpha = Math.round(color.alpha * 100) / 100;
  return alpha >= 1 ? `rgb(${red} ${green} ${blue})` : `rgba(${red} ${green} ${blue} / ${alpha})`;
}

async function loadOfferWithStorageCheck(
  standalone: boolean,
  nodeId: string,
  funnelHandoffId: string,
): Promise<OfferState> {
  const storageDocument = document as StorageAccessDocument;
  if (!standalone && storageDocument.hasStorageAccess !== undefined) {
    try {
      if (!(await storageDocument.hasStorageAccess())) return 'storage-required';
    } catch {
      return 'storage-required';
    }
  }
  try {
    const result = await fetchFunnelOffer(nodeId, funnelHandoffId);
    return result ?? (!standalone ? 'storage-denied' : 'empty');
  } catch (error) {
    if (!standalone) return 'storage-denied';
    throw error;
  }
}

interface PersistedPendingFunnel {
  readonly saleId: string | null;
  readonly voucher: CheckoutVoucher | null;
}

function initialEmbedState(standalone: boolean): {
  handoff: ReturnType<typeof readFunnelHandoff>;
  completed: boolean;
  completion: FunnelCompletion | null;
  consumeHash: boolean;
  pending: PersistedPendingFunnel | null;
} {
  const completed = standalone && isCompleteHandoff(window.location.hash);
  const hashHandoff = readFunnelHandoff(window.location.hash);
  if (completed) {
    clearFunnelHandoff();
    return { handoff: null, completed: true, completion: readFunnelCompletion(window.location.hash), consumeHash: true, pending: null };
  }
  const handoff = hashHandoff ?? restoreFunnelHandoff();
  if (hashHandoff !== null) persistFunnelHandoff(hashHandoff);
  return {
    handoff,
    completed: false,
    completion: null,
    consumeHash: hashHandoff !== null,
    pending: handoff === null ? null : restorePendingFunnel(handoff.handoffId, handoff.nodeId),
  };
}

function pendingStorageKey(handoffId: string, nodeId: string): string {
  return `${FUNNEL_PENDING_STORAGE_PREFIX}${handoffId}:${nodeId}`;
}

function persistPendingFunnel(
  handoffId: string,
  nodeId: string,
  saleId: string | null,
  voucher: CheckoutVoucher | null,
): void {
  try {
    window.sessionStorage.setItem(pendingStorageKey(handoffId, nodeId), JSON.stringify({ saleId, voucher }));
  } catch {
    return;
  }
}

function restorePendingFunnel(handoffId: string, nodeId: string): PersistedPendingFunnel | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(pendingStorageKey(handoffId, nodeId)) ?? 'null') as unknown;
    if (typeof value !== 'object' || value === null || !('saleId' in value) || (value.saleId !== null && typeof value.saleId !== 'string')) {
      return null;
    }
    const voucher = 'voucher' in value && typeof value.voucher === 'object'
      ? value.voucher as CheckoutVoucher | null
      : null;
    return { saleId: value.saleId, voucher };
  } catch {
    return null;
  }
}

function clearPendingFunnel(handoffId: string, nodeId: string): void {
  try {
    window.sessionStorage.removeItem(pendingStorageKey(handoffId, nodeId));
  } catch {
    return;
  }
}
